import { FormControlLabel, ListItemText, Skeleton, Stack, Typography } from "@mui/material";
import { GeofenceLocation } from "@verity/proctoring-option/types";
import L, { Circle, LatLngExpression, Map } from "leaflet";
import { LocateControl } from "leaflet.locatecontrol";
import "leaflet.locatecontrol/dist/L.Control.Locate.min.css";
import "leaflet/dist/leaflet.css";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
	convertFeetToMeters,
	convertGeolocationPositionErrorToFailReason,
	convertMetersToFeet,
} from "../../../before-quiz/steps/system-check";
import { CoreButton, CoreMenuItem, CoreModal, CoreSelect, CoreSlider, CoreTextField, coreToast } from "../../../core";
import { useMobileLayout } from "../../../hooks";
import i18nPackage from "../../../i18n/i18n";
import { useEditQuizStyles } from "../../components";
import { ResponsiveModalBody } from "../../components/EditQuizModalComponents";
import { useEditQuizToast } from "../../context/EditQuizContext";
import MapPin from "../../images/configuration/map-pin.svg";
import styles from "./GeofenceModal.module.css";

const DEFAULT_RADIUS_M = 10;
const DEFAULT_LNG = 0;
const DEFAULT_LAT = 0;
const OPEN_STREET_MAP_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OPEN_STREET_MAP_ATTRIBUTION =
	'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const OPEN_STREET_MAP_REFERRER = "https://yuja.com";
const OPEN_STREET_MAP_REFERRER_POLICY = "origin";

class ReferrerTileLayer extends L.TileLayer {
	protected createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
		const tile = document.createElement("img");
		const size = this.getTileSize();
		tile.width = size.x;
		tile.height = size.y;
		tile.className = "leaflet-tile";
		tile.alt = "";
		tile.setAttribute("role", "presentation");

		void fetch(this.getTileUrl(coords), {
			referrer: OPEN_STREET_MAP_REFERRER,
			referrerPolicy: OPEN_STREET_MAP_REFERRER_POLICY,
		})
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`Failed to load map tile: ${response.status}`);
				}

				const tileUrl = URL.createObjectURL(await response.blob());

				tile.onload = () => {
					URL.revokeObjectURL(tileUrl);
					done(undefined, tile);
				};
				tile.onerror = () => {
					URL.revokeObjectURL(tileUrl);
					done(new Error("Failed to render map tile"), tile);
				};
				tile.src = tileUrl;
			})
			.catch((error) => {
				done(error instanceof Error ? error : new Error(String(error)), tile);
			});

		return tile;
	}
}

function getMinRadius(isMetric: boolean) {
	const minRadiusMeters = 10;
	return isMetric ? minRadiusMeters : convertMetersToFeet(minRadiusMeters);
}

function getMaxRadius(isMetric: boolean) {
	const maxRadiusMeters = 10000;
	return isMetric ? maxRadiusMeters : convertMetersToFeet(maxRadiusMeters);
}

type GeofenceToolbarProps = {
	geofence: GeofenceLocationWithError;
	allowEdit: boolean;
	getGeofenceError: (geofence: GeofenceLocationWithError) => GeofenceError | undefined;
	updateGeofence: (newGeofence: GeofenceLocationWithError) => void;
};

function GeofenceToolbar({ geofence, allowEdit, getGeofenceError, updateGeofence }: GeofenceToolbarProps) {
	const [name, setName] = useState<string>(geofence.name);
	const [error, setError] = useState<GeofenceError | undefined>(geofence.error);
	// For typing so we dont update parent on every key down
	const [radiusValue, setRadiusValue] = useState<string>(String(geofence.radius));
	const toastContainerId = useEditQuizToast();

	const errorMessage = error ? getErrorMessage(error) : undefined;

	// If value ever changes, sync state
	useEffect(() => {
		setRadiusValue(String(geofence.radius));
	}, [geofence.radius]);

	const { isMetric } = geofence;

	const minRadius = getMinRadius(isMetric);
	const maxRadius = getMaxRadius(isMetric);

	const metricLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.metricUnit");
	const imperialLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.imperialUnit");
	const unitLabel = isMetric ? metricLabel : imperialLabel;

	return (
		<div className={styles.geofenceRow}>
			<FormControlLabel
				control={
					<CoreTextField
						onChange={(e) => {
							const newName = e.target.value;
							setName(newName);
							const newError = getGeofenceError({ ...geofence, name: newName });
							setError(newError);
							updateGeofence({ ...geofence, name: newName, error: newError });
						}}
						value={name}
						alwaysShowLength={true}
						maxLength={50}
						className={styles.geofenceLocationTextfield}
						size="small"
						disabled={!allowEdit}
						error={errorMessage}
					/>
				}
				label={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.locationLabel")}
				labelPlacement="top"
				className={styles.toolbarFormControl}
			/>
			<FormControlLabel
				control={
					<CoreTextField
						onChange={(e) => {
							const value = e.target.value;
							setRadiusValue(value);
							if (isValidRadius(value, isMetric)) {
								updateGeofence({ ...geofence, radius: Number(value) });
							}
						}}
						onBlur={(e) => {
							if (!isValidRadius(e.target.value, isMetric)) {
								updateGeofence({ ...geofence, radius: minRadius });
								setRadiusValue(String(minRadius));
								coreToast.dismiss();
								coreToast.error(
									i18nPackage.t("editQuizPage.configurationStep.geofenceModal.error.invalidRadius", {
										minRadius: minRadius.toLocaleString(),
										maxRadius: maxRadius.toLocaleString(),
										unit: unitLabel,
									}),
									toastContainerId ? { containerId: toastContainerId } : undefined,
								);
							}
						}}
						className={styles.radiusTextField}
						value={radiusValue}
						size="small"
						disabled={!allowEdit}
					/>
				}
				label={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radius")}
				labelPlacement="top"
				className={styles.toolbarFormControl}
			/>
			<FormControlLabel
				control={
					<CoreSelect<string>
						renderValue={(value) => {
							const translationKey = value === "metricUnit" ? "metricUnit" : "imperialUnit"; // To satisfy typescript
							return <span>{i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.${translationKey}`)}</span>;
						}}
						value={isMetric ? "metricUnit" : "imperialUnit"}
						onChange={(e) => {
							const value = e.target.value;
							const isMetric = value === "metricUnit";
							const newRadius = isMetric ? convertFeetToMeters(geofence.radius) : convertMetersToFeet(geofence.radius);

							updateGeofence({ ...geofence, radius: newRadius, isMetric });
						}}
						disabled={!allowEdit}
						className={styles.unitDropdown}
						aria-label={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.unitSelectAriaLabel")}
					>
						<CoreMenuItem value="metricUnit">
							<ListItemText primary={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.metricUnit")} />
						</CoreMenuItem>
						<CoreMenuItem value="imperialUnit">
							<ListItemText primary={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.imperialUnit")} />
						</CoreMenuItem>
					</CoreSelect>
				}
				label={i18nPackage.t("editQuizPage.configurationStep.geofenceModal.unit")}
				labelPlacement="top"
				className={styles.toolbarFormControl}
			/>
		</div>
	);
}

function isValidRadius(radius: string | number, isMetric: boolean) {
	const minRadius = getMinRadius(isMetric);
	const maxRadius = getMaxRadius(isMetric);

	if (typeof radius === "number") return radius >= minRadius && radius <= maxRadius;
	if (!radius.trim()) return false;

	const numRadius = Number(radius);
	if (!Number.isFinite(numRadius)) return false;

	return numRadius >= minRadius && numRadius <= maxRadius;
}

function RadiusSlider(props: {
	onChange: (radius: number) => void;
	geofence: GeofenceLocationWithError;
	allowEdit: boolean;
}) {
	const { onChange, allowEdit, geofence } = props;
	const { isMetric, radius } = geofence;
	const sliderId = useId();

	const minRadius = getMinRadius(isMetric);
	const maxRadius = getMaxRadius(isMetric);

	const metricLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.metricUnitShort");
	const imperialLabel = i18nPackage.t("editQuizPage.configurationStep.geofenceModal.imperialUnitShort");
	const unitLabel = isMetric ? metricLabel : imperialLabel;

	return (
		<div className={styles.radiusContainer}>
			<div className={`${styles.radiusRow} ${styles.header}`}>
				<Typography className={styles.title} id={sliderId}>
					{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radiusSliderLabel")}
				</Typography>
				<Typography className={styles.description}>
					{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radiusSliderDesc", {
						minRadius: minRadius.toLocaleString(),
						maxRadius: maxRadius.toLocaleString(),
						unit: unitLabel,
					})}
				</Typography>
			</div>
			<CoreSlider
				onChange={onChange}
				value={radius}
				thumbShape="circle"
				ariaLabelledBy={sliderId}
				min={minRadius}
				max={maxRadius}
				disabled={!allowEdit}
			/>
			<div className={styles.radiusRow}>
				<Typography className={styles.radiusLabel}>
					{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radiusWithUnit", {
						radius: minRadius.toLocaleString(),
						unit: unitLabel,
					})}
				</Typography>
				<Typography className={styles.radiusLabel}>
					{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.radiusWithUnit", {
						radius: maxRadius.toLocaleString(),
						unit: unitLabel,
					})}
				</Typography>
			</div>
		</div>
	);
}

function getGeofenceError(
	geofence: GeofenceLocationWithError,
	geofences: GeofenceLocationWithError[],
): GeofenceError | undefined {
	if (!geofence.name.trim()) return "empty";

	const geofencesFiltered = geofences.filter((g) => g.id !== geofence.id);

	const duplicateName = geofencesFiltered.some((g) => g.name === geofence.name);

	return duplicateName ? "duplicate" : undefined;
}

function getErrorMessage(error: GeofenceError) {
	const errorKey = error === "duplicate" ? "duplicateName" : "emptyName";
	return i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.error.${errorKey}`);
}

function GeofenceMap({
	geofence,
	onUpdate,
	allowEdit,
}: {
	geofence: GeofenceLocation;
	onUpdate: (longitude: number, latitude: number) => void;
	allowEdit: boolean;
}) {
	const toastContainerId = useEditQuizToast();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const mapRef = useRef<Map | null>(null);
	const circleRef = useRef<Circle | null>(null);
	const markerRef = useRef<L.Marker | null>(null); // Optional: for center point

	const INITIAL_CENTER: LatLngExpression = [geofence.latitude, geofence.longitude];
	const INITIAL_ZOOM = 1;

	useEffect(() => {
		// 1. Create the map instance
		if (!containerRef.current || mapRef.current) return;

		const map = L.map(containerRef.current).setView(INITIAL_CENTER, INITIAL_ZOOM);
		mapRef.current = map;

		// 2. Add the Tile Layer (Map visuals)
		new ReferrerTileLayer(OPEN_STREET_MAP_TILE_URL, {
			maxZoom: 19,
			attribution: OPEN_STREET_MAP_ATTRIBUTION,
		}).addTo(map);

		// 3. Add the circle -> conver to meters if in feet
		const initialCircle = L.circle(INITIAL_CENTER, {
			radius: geofence.isMetric ? geofence.radius : convertFeetToMeters(geofence.radius),
			color: "rgb(66, 41, 110)",
		}).addTo(map);
		circleRef.current = initialCircle;

		// 4. Add initial Marker
		const initialMarker = L.marker(INITIAL_CENTER, {
			icon: L.icon({ iconUrl: MapPin, iconSize: [64, 64], iconAnchor: [32, 64] }),
		}).addTo(map);
		markerRef.current = initialMarker;

		new LocateControl({
			flyTo: false,
			setView: "untilPan",
		}).addTo(map);

		map.on("click", (e: L.LeafletMouseEvent) => {
			if (!allowEdit) return;
			const { lat, lng } = e.latlng;
			onUpdate(lng, lat);
		});

		map.on("locationerror", (e) => {
			const error = convertGeolocationPositionErrorToFailReason(e);
			const errorKey = error === "notInRadius" || error === "timeout" ? "unknown" : "noPermission";
			coreToast.error(
				i18nPackage.t(`editQuizPage.configurationStep.geofenceModal.error.${errorKey}`),
				toastContainerId ? { containerId: toastContainerId } : undefined,
			);
		});

		map.whenReady(() => {
			setTimeout(() => {
				if (geofence.longitude === DEFAULT_LNG || geofence.latitude === DEFAULT_LAT) {
					map.setZoom(3);
				}
			}, 100);
		});

		// 5. Cleanup function: removes the map instance when the component unmounts
		return () => {
			map.remove();
			mapRef.current = null;
		};
	}, []);

	// Updates circle
	useEffect(() => {
		const map = mapRef.current;
		const circle = circleRef.current;
		const marker = markerRef.current;

		if (map && circle && marker) {
			const newCenter: LatLngExpression = [geofence.latitude, geofence.longitude];

			// 1. Update the Circle's geometry
			circle.setLatLng(newCenter);

			circle.setRadius(geofence.isMetric ? geofence.radius : convertFeetToMeters(geofence.radius));

			// 2. Update the Marker's position
			marker.setLatLng(newCenter);

			// Move map to circle and make sure circle fits in map
			map.flyTo(newCenter, map.getZoom(), { duration: 0.5 });
			map.fitBounds(circle.getBounds(), { padding: [50, 50], duration: 0.8 });
		}
	}, [geofence]);

	return <div id="map" className={styles.geofenceMap} ref={containerRef} />;
}

function getDefaultGeofence(userLocation?: GeofenceLocation): GeofenceLocationWithError {
	const radius = userLocation?.radius
		? userLocation.radius
		: userLocation?.isMetric
			? DEFAULT_RADIUS_M
			: convertMetersToFeet(DEFAULT_RADIUS_M);

	return {
		latitude: userLocation?.latitude ?? DEFAULT_LAT,
		longitude: userLocation?.longitude ?? DEFAULT_LNG,
		radius: radius,
		name: i18nPackage.t("editQuizPage.configurationStep.geofenceModal.manualLocation"),
		id: crypto.randomUUID(),
		isMetric: userLocation?.isMetric ?? !isUserInUS(),
	};
}

function isUserInUS() {
	try {
		const zone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;

		if (!zone) return false;

		// Common US-specific timezones
		const usZones = [
			"America/New_York",
			"America/Chicago",
			"America/Denver",
			"America/Los_Angeles",
			"America/Phoenix",
			"America/Anchorage",
			"America/Honolulu",
		];

		return usZones.includes(zone);
	} catch (e) {
		// Fallback to locale if Intl fails
		return navigator.language === "en-US";
	}
}

function GeolocationSkeleton() {
	return (
		<Stack spacing={1} width={"100%"}>
			<Skeleton variant="rectangular" width={"100%"} />
			<Stack direction={"row"} width={"100%"} spacing={3}>
				<Skeleton variant="rectangular" width={"350px"} height={510} />
				<Stack spacing={1} width={"450px"}>
					<Skeleton variant="rectangular" width={"100%"} height={300} />
					<Skeleton variant="rectangular" width={"100%"} height={200} />
				</Stack>
			</Stack>
		</Stack>
	);
}

function getGeofenceTitle(allowEdit: boolean, geofence?: GeofenceLocationWithId) {
	if (!allowEdit) {
		return i18nPackage.t("editQuizPage.configurationStep.geofenceModal.viewTitle");
	} else if (geofence) {
		return i18nPackage.t("editQuizPage.configurationStep.geofenceModal.editTitle");
	}
	return i18nPackage.t("editQuizPage.configurationStep.geofenceModal.addTitle");
}

export type GeofenceLocationWithId = GeofenceLocation & { id: string };

type GeofenceError = "duplicate" | "empty";

type GeofenceLocationWithError = GeofenceLocation & {
	id: string;
	error?: GeofenceError;
};

type GeofenceModalProps = {
	onClose: () => void;
	allowEdit: boolean;
	geofence?: GeofenceLocationWithId;
	presetGeofences: GeofenceLocationWithId[];
	onSave?: (geofence: GeofenceLocationWithId) => void;
};

export default function GeofenceModal(props: GeofenceModalProps) {
	const { onClose, onSave, geofence, presetGeofences, allowEdit } = props;
	const isMobileLayout = useMobileLayout();
	const { getEditQuizModalWidth, getModalInModalProps } = useEditQuizStyles();
	const [isFetching, setIsFetching] = useState<boolean>(false);

	// What user is editing
	const [newGeofence, setNewGeofence] = useState<GeofenceLocationWithError>(props.geofence ?? getDefaultGeofence());

	const isAdding = allowEdit && !geofence;

	// Try to get user's location
	useEffect(() => {
		const getPosition = (): Promise<GeolocationPosition> => {
			return new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					timeout: 3000,
					maximumAge: 0,
					enableHighAccuracy: false,
				});
			});
		};

		const getUserLocation = async () => {
			try {
				setIsFetching(true);
				const { accuracy, latitude, longitude } = (await getPosition()).coords;
				const isMetric = !isUserInUS();
				const roundedRadius = Number(accuracy.toFixed(0));
				const radius = isMetric ? roundedRadius : convertMetersToFeet(roundedRadius);

				const newGeo: GeofenceLocation = {
					...getDefaultGeofence(),
					isMetric,
					latitude,
					longitude,
					radius,
				};

				const finalGeofence: GeofenceLocationWithError = {
					...(geofence ? geofence : newGeo),
					id: geofence?.id ?? crypto.randomUUID(),
				};
				setNewGeofence(finalGeofence);
			} catch {
				if (!geofence) {
					setNewGeofence(getDefaultGeofence());
				}
			} finally {
				setIsFetching(false);
			}
		};

		void getUserLocation();
	}, []);

	const title = useMemo(() => {
		return getGeofenceTitle(allowEdit, geofence);
	}, [allowEdit, geofence]);

	return (
		<CoreModal
			{...getEditQuizModalWidth(isMobileLayout)}
			{...getModalInModalProps(isMobileLayout, title)}
			open
			onClose={onClose}
			buttons={
				<Stack direction={"row"} justifyContent={"flex-end"} spacing={1}>
					{allowEdit && (
						<CoreButton onClick={onClose} color="secondary">
							{i18nPackage.t("editQuizPage.configurationStep.geofenceModal.cancel")}
						</CoreButton>
					)}
					<CoreButton
						onClick={() => {
							if (allowEdit) {
								onSave?.(newGeofence);
							} else {
								onClose();
							}
						}}
						disabled={allowEdit ? Boolean(newGeofence.error) : false}
					>
						{isAdding
							? i18nPackage.t("editQuizPage.configurationStep.geofenceModal.add")
							: allowEdit
								? i18nPackage.t("editQuizPage.configurationStep.geofenceModal.save")
								: i18nPackage.t("editQuizPage.configurationStep.geofenceModal.close")}
					</CoreButton>
				</Stack>
			}
		>
			<ResponsiveModalBody
				title={title}
				description={
					isAdding ? i18nPackage.t("editQuizPage.configurationStep.geofenceModal.addDescription") : undefined
				}
			>
				{isFetching && <GeolocationSkeleton />}
				{!isFetching && (
					<div className={styles.geofenceContainer}>
						<GeofenceToolbar
							allowEdit={allowEdit}
							geofence={newGeofence}
							updateGeofence={setNewGeofence}
							getGeofenceError={(g) => getGeofenceError(g, presetGeofences)}
						/>
						<RadiusSlider
							allowEdit={allowEdit}
							geofence={newGeofence}
							onChange={(radius) => setNewGeofence((prev) => ({ ...prev, radius }))}
						/>
						<GeofenceMap
							allowEdit={allowEdit}
							geofence={newGeofence}
							onUpdate={(lng, lat) => {
								setNewGeofence((prev) => ({ ...prev, latitude: lat, longitude: lng }));
							}}
						/>
					</div>
				)}
			</ResponsiveModalBody>
		</CoreModal>
	);
}
